import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  IconButton,
  Portal,
  CircularProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
} from '@mui/icons-material';
import {
  RichTextEditor,
  MenuControlsContainer,
  MenuDivider,
  MenuButtonBold,
  MenuButtonItalic,
  MenuButtonUnderline,
  MenuButtonStrikethrough,
  MenuButtonUndo,
  MenuButtonRedo,
  MenuButtonBulletedList,
  MenuButtonOrderedList,
} from 'mui-tiptap';
import { StarterKit } from '@tiptap/starter-kit';
import { Underline } from '@tiptap/extension-underline';
import { Placeholder } from '@tiptap/extension-placeholder';
import { modalStyles } from '../styles/modalStyles';
import { createSignatureSalutation, CreateSignatureSalutationRequest } from '../services/emailService';
import { useToast } from '../contexts/ToastContext';
import { STORAGE_KEYS } from '../helpers/constants';

interface AddSignatureAndSalutationModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: SignatureAndSalutationData) => void;
  initialData?: SignatureAndSalutationData;
  contentOnly?: boolean; // New prop to show only content without modal wrapper
  onDataChange?: (data: SignatureAndSalutationData) => void; // For content-only mode
  channelId: string; // Required channel ID for API call
  isEditing?: boolean; // New prop to indicate edit mode
}

export interface SignatureAndSalutationData {
  id?: string; // API-generated ID for existing signatures
  name: string; // Plain text name
  salutation: string; // Plain text salutation
  signature: string; // HTML signature (rich text)
  is_default: boolean;
  channel_id?: string; // Channel ID for API calls
}

const AddSignatureAndSalutationModal: React.FC<AddSignatureAndSalutationModalProps> = ({
  open,
  onClose,
  onSave,
  initialData,
  contentOnly = false,
  onDataChange,
  channelId,
  isEditing = false
}) => {
  const [formData, setFormData] = useState<SignatureAndSalutationData>({
    name: initialData?.name || '',
    signature: initialData?.signature || '',
    salutation: initialData?.salutation || '',
    is_default: initialData?.is_default ?? true,
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const { showSuccess, showError } = useToast();

  // Helper function to check if rich text content is actually empty
  const isRichTextEmpty = (content: string): boolean => {
    if (!content || content.trim() === '') return true;
    // Remove HTML tags and check if there's actual text content
    const textContent = content.replace(/<[^>]*>/g, '').trim();
    return textContent === '';
  };

  // Memoized validation to ensure it updates when formData changes
  const isFormValid = useMemo(() => {
    // Name is optional, but at least one of salutation or signature is required
    const hasSalutation = formData.salutation.trim() !== '';
    const hasSignature = !isRichTextEmpty(formData.signature);
    
    return hasSalutation || hasSignature;
  }, [formData.salutation, formData.signature]);

  const handleInputChange = (field: keyof SignatureAndSalutationData, value: any) => {
    const newData = {
      ...formData,
      [field]: value
    };
    setFormData(newData);
    
    // If in content-only mode, call onDataChange
    if (contentOnly && onDataChange) {
      onDataChange(newData);
    }
  };

  const handleSave = async () => {
    // Use the validation helper function
    if (!isFormValid) {
      return;
    }

    // Don't make API call in content-only mode
    if (contentOnly) {
      onSave(formData);
      onClose();
      return;
    }

    // Show loading state for edit mode too
    if (isEditing) {
      setIsLoading(true);
      try {
        onSave(formData);
        // Add a small delay to show the loading state
        await new Promise(resolve => setTimeout(resolve, 500));
        onClose();
      } finally {
        setIsLoading(false);
      }
      return;
    }

    const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || '';
    
    if (!token) {
      showError('Authentication required. Please log in again.');
      return;
    }

    setIsLoading(true);
    
    try {
      const apiData: CreateSignatureSalutationRequest = {
        channel_id: channelId,
        name: formData.name,
        signature: formData.signature,
        salutation: formData.salutation,
        is_default: formData.is_default,
      };

      const response = await createSignatureSalutation(apiData, token);
      
      if (response.success) {
        showSuccess('Signature and salutation saved successfully!');
        onSave(formData);
        onClose();
      } else {
        showError(response.message || 'Failed to save signature and salutation');
      }
    } catch (error) {
      console.error('Error saving signature and salutation:', error);
      showError(error instanceof Error ? error.message : 'Failed to save signature and salutation');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      signature: '',
      salutation: '',
      is_default: true,
    });
    onClose();
  };

  const formContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
      {/* Name Field */}
      <Box>
        <Typography variant="body2" sx={{ mb: 2, fontWeight: 500, color: 'text.primary' }}>
          What is the name for this signature?
        </Typography>
        <TextField
          fullWidth
          label="Name"
          value={formData.name}
          onChange={(e) => handleInputChange('name', e.target.value)}
          placeholder="Enter signature name"
          size="small"
        />
      </Box>

      {/* Salutation Field */}
      <Box>
        <Typography variant="body2" sx={{ mb: 2, fontWeight: 500, color: 'text.primary' }}>
          What salutation would you like to use?
        </Typography>
        <TextField
          fullWidth
          label="Salutation"
          value={formData.salutation}
          onChange={(e) => handleInputChange('salutation', e.target.value)}
          placeholder="Enter your salutation (optional if signature is provided)..."
          size="small"
        />
      </Box>

      {/* Signature Field */}
      <Box>
        <Typography variant="body2" sx={{ mb: 2, fontWeight: 500, color: 'text.primary' }}>
          What signature would you like to use?
        </Typography>
        <RichTextEditor
          extensions={[
            StarterKit.configure({
              underline: false,
            }),
            Underline,
            Placeholder.configure({
              placeholder: 'Enter your signature (optional if salutation is provided)...',
            }),
          ]}
          content={formData.signature}
          onUpdate={({ editor }) => {
            handleInputChange('signature', editor.getHTML());
          }}
          renderControls={() => (
            <MenuControlsContainer>
              <MenuButtonBold />
              <MenuButtonItalic />
              <MenuButtonUnderline />
              <MenuButtonStrikethrough />
              <MenuDivider />
              <MenuButtonUndo />
              <MenuButtonRedo />
              <MenuDivider />
              <MenuButtonBulletedList />
              <MenuButtonOrderedList />
            </MenuControlsContainer>
          )}
          sx={{
            minHeight: 120,
            '& .ProseMirror': {
              minHeight: 120,
              padding: 1.5,
            },
          }}
        />
      </Box>

      {/* Is Default Checkbox - Only show when not in content-only mode */}
      {!contentOnly && (
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.is_default}
              onChange={(e) => handleInputChange('is_default', e.target.checked)}
            />
          }
          label="Is Default"
        />
      )}
    </Box>
  );

  // If contentOnly mode, return just the form content
  if (contentOnly) {
    return formContent;
  }

  // Otherwise, return the full modal
  return (
    <Portal container={document.body}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        disablePortal={false}
        PaperProps={{
          sx: {
            ...modalStyles.dialog['& .MuiDialog-paper'],
            position: 'relative !important',
            zIndex: '10000 !important',
          },
        }}
        sx={{
          zIndex: '10000 !important',
          '& .MuiBackdrop-root': {
            zIndex: '9999 !important',
            backgroundColor: 'rgba(0, 0, 0, 0.5) !important',
          },
          '& .MuiDialog-container': {
            zIndex: '10000 !important',
          },
        }}
      >
      <DialogTitle sx={modalStyles.dialogTitle}>
        <Typography variant="h5" component="div">
          {isEditing ? 'Edit Signature & Salutation' : 'Add Signature & Salutation'}
        </Typography>
        <IconButton
          onClick={handleClose}
          sx={modalStyles.closeButton}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {formContent}
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button
          onClick={handleClose}
          sx={{ color: 'text.secondary' }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!isFormValid || isLoading}
          startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : undefined}
        >
          {isLoading ? (isEditing ? 'Updating...' : 'Saving...') : (isEditing ? 'Update' : 'Save')}
        </Button>
      </DialogActions>
      </Dialog>
    </Portal>
  );
};

export default AddSignatureAndSalutationModal;

